import React, { useState, useEffect } from "react";
import { useDeploymentGuard } from "@/features/omnimizer/useDeploymentGuard";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Divider,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Paper,
  Tooltip,
  Skeleton,
  Avatar,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stack,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  Http as HttpIcon,
  QuestionMark as QuestionMarkIcon,
  Code as CodeIcon,
  Language as LanguageIcon,
  CheckCircle as CheckCircleIcon,
  SupportAgent as SupportAgentIcon,
  BarChart as BarChartIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
  AdminPanelSettings as AdminIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import DashboardLayout from "../components/DashboardLayout";
import { deploymentApi } from "../services/omnimizer-api-service";
import { toast } from "sonner";
import { canModifyDeployments, isAdmin as hasAdminRole } from "@/features/omnimizer/permissions";
import { useProjects } from "@/contexts/ProjectContext";
import { saveCatalogIntent } from "@/features/omnimizer/catalog-sync";
import { CatalogTargetFields, validCatalogTarget } from "@/features/omnimizer/components/CatalogTargetFields";
import PredefinedDeploymentForm from "../components/PredefinedDeploymentForm";
import { useTranslation } from "react-i18next";
import { DeploymentsSubnav } from "@/features/omnimizer/components/DeploymentsSubnav";

// Source logo component
const SourceLogo = ({ source }) => {
  const { t } = useTranslation();

  // Determine which logo/icon to use based on source
  if (source === "hf") {
    return (
      <Avatar
        src="/providers/huggingface.svg"
        alt={t("deployments.huggingFace")}
        sx={{
          width: 32,
          height: 32,
          marginInlineEnd: 1,
          bgcolor: "var(--color-3)",
          border: "1px solid var(--color-71)",
        }}
      />
    );
  } else if (source === "s3") {
    return (
      <Avatar
        sx={{
          width: 32,
          height: 32,
          marginInlineEnd: 1,
          bgcolor: "var(--color-72)", // AWS orange color
        }}
      >
        <StorageIcon sx={{ fontSize: 18, color: "var(--color-3)" }} />
      </Avatar>
    );
  } else if (source === "http" || source === "https") {
    return (
      <Avatar
        sx={{
          width: 32,
          height: 32,
          marginInlineEnd: 1,
          bgcolor: "var(--color-73)", // Purple for HTTP
        }}
      >
        <HttpIcon sx={{ fontSize: 18, color: "var(--color-3)" }} />
      </Avatar>
    );
  } else if (source === "local") {
    return (
      <Avatar
        sx={{
          width: 32,
          height: 32,
          marginInlineEnd: 1,
          bgcolor: "var(--color-74)", // Green for local
        }}
      >
        <CodeIcon sx={{ fontSize: 18, color: "var(--color-3)" }} />
      </Avatar>
    );
  } else {
    // Default for unknown sources
    return (
      <Avatar
        sx={{
          width: 32,
          height: 32,
          marginInlineEnd: 1,
          bgcolor: "var(--color-71)",
        }}
      >
        <QuestionMarkIcon sx={{ fontSize: 18 }} />
      </Avatar>
    );
  }
};

// Animated skeleton card for loading state
const ModelSkeletonCard = () => (
  <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
    <CardContent sx={{ flexGrow: 1 }}>
      {/* Model name skeleton */}
      <Skeleton variant="text" width="70%" height={32} sx={{ mb: 0.5 }} />

      {/* Description skeletons */}
      <Skeleton variant="text" width="90%" />
      <Skeleton variant="text" width="85%" />

      {/* Divider */}
      <Divider sx={{ my: 2 }} />

      {/* Model details grid */}
      <Grid container spacing={2}>
        {/* Source */}
        <Grid item xs={6}>
          <Skeleton variant="text" width="60%" sx={{ mb: 0.5 }} />
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Skeleton
              variant="circular"
              width={32}
              height={32}
              sx={{ marginInlineEnd: 1 }}
            />
            <Skeleton variant="text" width="50%" />
          </Box>
        </Grid>

        {/* Type */}
        <Grid item xs={6}>
          <Skeleton variant="text" width="60%" sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="40%" />
        </Grid>

        {/* GPU Count */}
        <Grid item xs={6}>
          <Skeleton variant="text" width="60%" sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="30%" />
        </Grid>

        {/* GPU Type */}
        <Grid item xs={6}>
          <Skeleton variant="text" width="60%" sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="70%" />
        </Grid>
      </Grid>

      {/* Model URL */}
      <Box sx={{ mt: 2 }}>
        <Skeleton variant="text" width="100%" />
      </Box>
    </CardContent>

    {/* Deploy button */}
    <CardActions sx={{ p: 2, pt: 0 }}>
      <Skeleton
        variant="rectangular"
        width="100%"
        height={36}
        sx={{ borderRadius: 1 }}
      />
    </CardActions>
  </Card>
);

// Skeleton for search bar
const SearchBarSkeleton = () => (
  <Paper sx={{ p: 2, mb: 4 }}>
    <Grid container spacing={2} alignItems="center">
      <Grid item xs>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={40}
          sx={{ borderRadius: 1 }}
        />
      </Grid>
    </Grid>
  </Paper>
);

// Skeleton for header
const HeaderSkeleton = () => (
  <Box sx={{ mb: 4 }}>
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12} md={8}>
        <Skeleton variant="text" width="50%" height={40} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="70%" height={24} />
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
        sx={{
          display: "flex",
          justifyContent: { xs: "flex-start", md: "flex-end" },
          mt: { xs: 2, md: 0 },
        }}
      >
        <Skeleton
          variant="rectangular"
          width={120}
          height={36}
          sx={{ borderRadius: 1 }}
        />
      </Grid>
    </Grid>
  </Box>
);

const PredefinedModels = () => {
  const deploymentGuard = useDeploymentGuard();
  const { i18n, t } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const navigate = useNavigate();
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [deployingModelId, setDeployingModelId] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const isAdmin = hasAdminRole();
  const canDeploy = canModifyDeployments();
  const { selectedProjects, availableProjects = [] } = useProjects();
  const [pendingModel, setPendingModel] = useState(null);
  const [deploymentName, setDeploymentName] = useState("");
  const [catalogTarget, setCatalogTarget] = useState({ projectId: "", isPublic: false });

  // New state for admin menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuModel, setMenuModel] = useState(null);
  const [editModel, setEditModel] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingModelId, setDeletingModelId] = useState(null);

  const openMenu = Boolean(anchorEl);
  const getModelId = (model) => model?.id || model?._id || model?.model_id;

  const getModelSourceLabel = (source) => {
    switch (source) {
      case "hf":
        return t("deployments.huggingFace");
      case "s3":
        return t("deployments.s3Bucket");
      case "http":
      case "https":
        return t("deployments.httpUrl");
      case "local":
        return t("predefinedDeployment.local");
      default:
        return source || t("common.unknown");
    }
  };

  const getModelTypeLabel = (isLlm) =>
    isLlm ? t("deployments.llm") : t("predefinedModels.deepLearning");

  const getDisplayValue = (value) => value || t("common.notAvailable");

  useEffect(() => {
    fetchPredefinedModels();


  }, []);

  const fetchPredefinedModels = async () => {
    setLoading(true);
    try {
      const response = await deploymentApi.getPredefinedDeployments();
      setModels(response.data);
    } catch (error) {
      console.error("Error fetching predefined models:", error);
      toast.error(t("predefinedModels.failedToLoad"));
    } finally {
      setLoading(false);
    }
  };

  const handleDeployModel = (model, event) => {
    event?.stopPropagation();
    if (!canDeploy) return;
    setPendingModel(model);
    setDeploymentName(`${model.name.slice(0, 48)}-${Date.now().toString(36)}`);
    setCatalogTarget({ projectId: selectedProjects?.length === 1 && selectedProjects[0] !== "all" ? selectedProjects[0] : "", isPublic: false });
  };

  const handleCustomize = (model, event) => {
    event?.stopPropagation();
    if (canDeploy) navigate("/deployments/new?preset=" + encodeURIComponent(String(getModelId(model))));
  };

  const deploySelectedModel = async () => {
    const model = pendingModel;
    if (!canDeploy || !model || !deploymentName.trim() || deployingModelId !== null || !validCatalogTarget(catalogTarget, availableProjects, isAdmin)) return;

    setDeployingModelId(getModelId(model) || model.name);
    try {
      // Create deployment configuration based on predefined model
      const deploymentConfig = {
        name: deploymentName.trim(),
        namespace: model.namespace || "default",
        max_replicas: model.max_replicas || 1,
        model_source: model.model_source,
        model_url: model.model_url,
        args: model.args,
        resources: model.resources,
        credentials: model.credentials,
        ...(model.cluster_id || model.model_metadata?.cluster_id ? { cluster_id: model.cluster_id || model.model_metadata.cluster_id } : {}),
      };

      // Call API to create deployment
      setPendingModel(null);
      const response = await deploymentGuard.run(deploymentConfig, () => deploymentApi.createDeployment(deploymentConfig));
      if (!response) return;
      saveCatalogIntent(response.data.model_id, { ...catalogTarget, name: deploymentConfig.name });
      setPendingModel(null);
      toast.success(t("predefinedModels.deploySuccess", { name: deploymentConfig.name }));
      navigate(`/deployments/${response.data.model_id}`);
    } catch (error) {
      console.error("Error deploying model:", error);
      toast.error(
        t("predefinedModels.deployFailed", {
          name: model.name,
          message: error.response?.data?.detail || error.message,
        }),
      );
    } finally {
      setDeployingModelId(null);
    }
  };

  const handleCardClick = (model) => {
    setSelectedModel(model);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
  };

  const handleOpenForm = () => {
    setEditModel(null); // Reset edit model when creating new
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditModel(null);
    // Refresh the list after adding or editing a predefined deployment
    fetchPredefinedModels();
  };

  // Admin menu handlers
  const handleMenuOpen = (event, model) => {
    event.stopPropagation(); // Prevent card click
    setAnchorEl(event.currentTarget);
    setMenuModel(model);
  };

  const handleMenuClose = (event) => {
    if (event) {
      event.stopPropagation(); // Prevent card click
    }
    setAnchorEl(null);
    // Don't reset menuModel here to preserve it for delete/edit operations
  };

  const handleEditModel = (event) => {
    event.stopPropagation(); // Prevent card click
    setEditModel(menuModel);
    setFormOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = (event) => {
    event.stopPropagation(); // Prevent card click
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    // Check if model exists and has an ID (use the appropriate ID property)
    if (!menuModel) {
      toast.error(t("predefinedModels.invalidModelSelected"));
      setDeleteDialogOpen(false);
      return;
    }

    // Determine which property contains the ID based on the API response structure
    const modelId = menuModel.id || menuModel._id || menuModel.model_id;

    if (!modelId) {
      console.error("Cannot find ID in model object:", menuModel);
      toast.error(t("predefinedModels.missingModelId"));
      setDeleteDialogOpen(false);
      return;
    }

    setDeletingModelId(modelId);
    try {
      // Call the delete endpoint with the model ID
      await deploymentApi.deletePredefinedDeployment(modelId);

      toast.success(
        t("predefinedModels.deleteSuccess", { name: menuModel.name }),
      );

      // Refresh the models list
      fetchPredefinedModels();
    } catch (error) {
      console.error("Error deleting predefined model:", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        t("common.unknownErrorOccurred");
      toast.error(
        t("predefinedModels.deleteFailed", {
          name: menuModel.name,
          message: errorMessage,
        }),
      );
    } finally {
      setDeletingModelId(null);
      setDeleteDialogOpen(false);
      setMenuModel(null);
    }
  };

  // Filter models based on search term
  const filteredModels = models.filter(
    (model) =>
      model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.model_url.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.model_metadata?.provider
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      model.model_metadata?.languages?.some((lang) =>
        lang.toLowerCase().includes(searchTerm.toLowerCase()),
      ) ||
      model.model_metadata?.best_for?.some((use) =>
        use.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
  );

  // Render model metadata in drawer
  const renderModelMetadata = () => {
    if (!selectedModel) return null;

    const metadata = selectedModel.model_metadata || {};

    return (
      <Box sx={{ p: 3, width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography variant="h5" component="h2">
            {selectedModel.name}
          </Typography>
          <IconButton onClick={handleCloseDrawer}>
            <CloseIcon />
          </IconButton>
        </Box>

        {metadata.flags && metadata.flags.includes("Staff pick") && (
          <Chip
            icon={<CheckCircleIcon />}
            label={t("predefinedModels.staffPick")}
            color="primary"
            sx={{ mb: 2 }}
          />
        )}

        <Typography variant="body1" paragraph>
          {selectedModel.description || t("predefinedModels.noDescriptionAvailable")}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t("predefinedModels.modelInformation")}
        </Typography>

        <List disablePadding>
          {metadata.provider && (
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <BusinessIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t("predefinedModels.provider")}
                secondary={metadata.provider}
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                }}
                secondaryTypographyProps={{ variant: "body1" }}
              />
            </ListItem>
          )}

          {metadata.model_size && (
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <BarChartIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t("predefinedModels.modelSize")}
                secondary={metadata.model_size}
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                }}
                secondaryTypographyProps={{ variant: "body1" }}
              />
            </ListItem>
          )}

          {metadata.languages && metadata.languages.length > 0 && (
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <LanguageIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t("predefinedModels.languages")}
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                }}
              />
            </ListItem>
          )}

          {metadata.languages && metadata.languages.length > 0 && (
            <Box sx={{ marginInlineStart: 4, mb: 2 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {metadata.languages.map((lang, idx) => (
                  <Chip key={idx} label={lang} size="small" />
                ))}
              </Stack>
            </Box>
          )}

          {metadata.best_for && metadata.best_for.length > 0 && (
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckCircleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t("predefinedModels.bestFor")}
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                }}
              />
            </ListItem>
          )}

          {metadata.best_for && metadata.best_for.length > 0 && (
            <Box sx={{ marginInlineStart: 4, mb: 2 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {metadata.best_for.map((use, idx) => (
                  <Chip key={idx} label={use} size="small" color="success" />
                ))}
              </Stack>
            </Box>
          )}

          {metadata.supports && metadata.supports.length > 0 && (
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <SupportAgentIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t("predefinedModels.supports")}
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                }}
              />
            </ListItem>
          )}

          {metadata.supports && metadata.supports.length > 0 && (
            <Box sx={{ marginInlineStart: 4, mb: 2 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {metadata.supports.map((support, idx) => (
                  <Chip key={idx} label={support} size="small" />
                ))}
              </Stack>
            </Box>
          )}
        </List>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t("predefinedModels.technicalDetails")}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              {t("predefinedModels.source")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
              <SourceLogo source={selectedModel.model_source} />
              <Typography variant="body1">
                {getModelSourceLabel(selectedModel.model_source)}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              {t("predefinedModels.type")}
            </Typography>
            <Typography variant="body1">
              {getModelTypeLabel(selectedModel.args?.is_llm)}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              {t("predefinedModels.gpuCount")}
            </Typography>
            <Typography variant="body1">
              {selectedModel.resources?.gpu_count || 1}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              {t("predefinedModels.gpuType")}
            </Typography>
            <Typography variant="body1">
              {getDisplayValue(selectedModel.resources?.gpu_type)}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              {t("predefinedModels.memory")}
            </Typography>
            <Typography variant="body1">
              {getDisplayValue(selectedModel.resources?.memory_request)}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              {t("predefinedModels.storage")}
            </Typography>
            <Typography variant="body1">
              {getDisplayValue(selectedModel.resources?.storage)}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t("predefinedModels.modelUrl")}
        </Typography>
        <Typography
          variant="body1"
          component="div"
          sx={{
            wordBreak: "break-all",
            mb: 3,
          }}
        >
          {selectedModel.model_url}
        </Typography>

        {canDeploy && <Button variant="outlined" fullWidth sx={{ mt: 2 }} onClick={(event) => handleCustomize(selectedModel, event)}>{t("omnimizer.quick.customize")}</Button>}
        <Button
          variant="contained"
          fullWidth
          startIcon={
            deployingModelId === (getModelId(selectedModel) || selectedModel.name) ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <MemoryIcon />
            )
          }
          onClick={(e) => handleDeployModel(selectedModel, e)}
          disabled={!canDeploy || deployingModelId !== null}
          sx={{ mt: 2 }}
        >
          {deployingModelId === (getModelId(selectedModel) || selectedModel.name)
            ? t("predefinedModels.deploying")
            : t("predefinedModels.deployModel")}
        </Button>
      </Box>
    );
  };

  return (
    <DashboardLayout title={t("omnimizer.nav.quick")}>
      <Container maxWidth="xl">
        <DeploymentsSubnav current="quick" className="mb-5" />
        {loading ? (
          // Show skeleton loaders while loading
          <>
            <HeaderSkeleton />
            <SearchBarSkeleton />
            <Grid container spacing={3}>
              {Array.from({ length: 6 }).map((_, index) => (
                <Grid item xs={12} sm={6} md={4} key={`skeleton-${index}`}>
                  <ModelSkeletonCard />
                </Grid>
              ))}
            </Grid>
          </>
        ) : (
          <>
            <Box sx={{ mb: 4 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="h4" component="h1" gutterBottom>
                    {t("omnimizer.nav.quick")}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {t("predefinedModels.subtitle")}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    display: "flex",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                    gap: 2,
                    mt: { xs: 2, md: 0 },
                  }}
                >
                  {isAdmin ? (
                    <Tooltip title={t("predefinedModels.adminCreateTooltip")}>
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<AdminIcon />}
                        onClick={handleOpenForm}
                        sx={{
                          backgroundColor: (theme) =>
                            theme.palette.mode === "light"
                              ? "rgba(19, 211, 158, 0.08)"
                              : "rgba(16, 185, 129, 0.08)",
                          borderColor: (theme) =>
                            theme.palette.mode === "light"
                              ? "var(--color-75)"
                              : "var(--color-65)",
                          color: (theme) =>
                            theme.palette.mode === "light"
                              ? "var(--color-1)"
                              : "var(--color-65)",
                          "&:hover": {
                            backgroundColor: (theme) =>
                              theme.palette.mode === "light"
                                ? "rgba(19, 211, 158, 0.12)"
                                : "rgba(16, 185, 129, 0.12)",
                            borderColor: (theme) =>
                              theme.palette.mode === "light"
                                ? "var(--color-1)"
                                : "var(--color-65)",
                          },
                        }}
                      >
                        {t("predefinedModels.addModel")}
                      </Button>
                    </Tooltip>
                  ) : null}

                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon className="rtl:scale-x-[-1]" />}
                    onClick={fetchPredefinedModels}
                  >
                    {t("common.refresh")}
                  </Button>
                </Grid>
              </Grid>
            </Box>

            <Paper sx={{ p: 2, mb: 4 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs>
                  <TextField
                    label={t("common.search")}
                    placeholder={t("predefinedModels.searchPlaceholder")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>

            {filteredModels.length > 0 ? (
              <Grid container spacing={2}>
                {filteredModels.map((model) => {
                  // Generate a key that works with any ID structure
                  const modelKey =
                    model.id ||
                    model._id ||
                    model.model_id ||
                    `model-${model.name}`;

                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={modelKey}>
                      <Card
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          transition: "transform 0.2s, box-shadow 0.2s",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)",
                            cursor: "pointer",
                          },
                        }}
                        onClick={() => handleCardClick(model)}
                      >
                        <CardContent
                          sx={{
                            flexGrow: 1,
                            position: "relative",
                            p: 2,
                            pb: 1,
                          }}
                        >
                          {/* Admin menu button */}
                          {isAdmin && (
                            <IconButton
                              aria-label={t("predefinedModels.modelOptions")}
                              aria-controls={
                                openMenu ? "model-menu" : undefined
                              }
                              aria-haspopup="true"
                              aria-expanded={openMenu ? "true" : undefined}
                              onClick={(e) => handleMenuOpen(e, model)}
                              sx={{
                                position: "absolute",
                                top: 8,
                                insetInlineEnd: 8,
                                zIndex: 2,
                              }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          )}

                          {model.model_metadata?.flags?.includes(
                            "Staff pick",
                          ) && (
                            <Chip
                              label={t("predefinedModels.staffPick")}
                              color="primary"
                              size="small"
                              sx={{
                                position: "absolute",
                                top: 0,
                                insetInlineEnd: isAdmin ? 40 : 0, // Adjust position if admin menu is present
                                zIndex: 1,
                              }}
                            />
                          )}

                          <Typography
                            variant="h6"
                            component="div"
                            sx={{ mb: 0.5 }}
                          >
                            {model.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mb: 1,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              height: "40px",
                            }}
                          >
                            {model.description ||
                              t("predefinedModels.noDescriptionAvailable")}
                          </Typography>

                          <Divider sx={{ mt: 1, mb: 1 }} />

                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {t("predefinedModels.source")}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  mt: 0.5,
                                }}
                              >
                                <SourceLogo source={model.model_source} />
                                <Typography variant="body2">
                                  {getModelSourceLabel(model.model_source)}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {t("predefinedModels.type")}
                              </Typography>
                              <Typography variant="body2">
                                {getModelTypeLabel(model.args?.is_llm)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {t("predefinedModels.gpuCount")}
                              </Typography>
                              <Typography variant="body2">
                                {model.resources?.gpu_count || 1}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {t("predefinedModels.gpuType")}
                              </Typography>
                              <Typography variant="body2">
                                {getDisplayValue(model.resources?.gpu_type)}
                              </Typography>
                            </Grid>
                          </Grid>

                          <Grid container spacing={1} sx={{ mt: 0.5 }}>
                            <Grid item xs={6}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {t("predefinedModels.provider")}
                              </Typography>
                              <Typography variant="body2" noWrap>
                                {getDisplayValue(model.model_metadata?.provider)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {t("predefinedModels.modelSize")}
                              </Typography>
                              <Typography variant="body2">
                                {getDisplayValue(model.model_metadata?.model_size)}
                              </Typography>
                            </Grid>
                          </Grid>

                          <Box sx={{ mt: 1 }}>
                            <Tooltip title={model.model_url}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {model.model_url}
                              </Typography>
                            </Tooltip>
                          </Box>
                        </CardContent>
                        <CardActions sx={{ p: 2, pt: 0, gap: 1, flexWrap: "wrap" }}>
                          {canDeploy && <Button variant="outlined" onClick={(event) => handleCustomize(model, event)}>{t("omnimizer.quick.customize")}</Button>}
                          <Button
                            variant="contained"
                            fullWidth
                            startIcon={
                              deployingModelId === (getModelId(model) || model.name) ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                <MemoryIcon />
                              )
                            }
                            onClick={(e) => handleDeployModel(model, e)}
                            disabled={!canDeploy || deployingModelId !== null}
                            size="small"
                          >
                            {deployingModelId === (getModelId(model) || model.name)
                              ? t("predefinedModels.deploying")
                              : t("predefinedModels.deployModel")}
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Paper sx={{ p: 4, textAlign: "center" }}>
                <Typography color="text.secondary" paragraph>
                  {searchTerm
                    ? t("predefinedModels.noMatchingModels")
                    : t("predefinedModels.noModelsAvailable")}
                </Typography>
                {canDeploy && <Button
                  variant="contained"
                  onClick={() => navigate("/deployments/new")}
                >
                  {t("omnimizer.nav.deploy")}
                </Button>}
              </Paper>
            )}
          </>
        )}
      </Container>

      {deploymentGuard.dialogs}
      {/* Right side drawer for model details */}
      <Dialog open={Boolean(pendingModel)} onClose={() => deployingModelId === null && setPendingModel(null)} fullWidth maxWidth="sm" aria-labelledby="quick-catalog-title">
        <DialogTitle id="quick-catalog-title">{t("omnimizer.quick.targetTitle")}</DialogTitle>
        <DialogContent>
          <p className="mb-4 text-sm text-muted-foreground">{t("omnimizer.quick.targetHint")}</p>
          <TextField fullWidth size="small" sx={{ mb: 2 }} label={t("omnimizer.wizard.deploymentName")} value={deploymentName} onChange={(event) => setDeploymentName(event.target.value)} disabled={deployingModelId !== null} />
          <CatalogTargetFields prefix="quick-catalog" value={catalogTarget} onChange={setCatalogTarget} projects={availableProjects} admin={isAdmin} />
        </DialogContent>
        <DialogActions>
          <Button disabled={deployingModelId !== null} onClick={() => setPendingModel(null)}>{t("common.cancel")}</Button>
          <Button variant="contained" disabled={!canDeploy || !deploymentName.trim() || deployingModelId !== null || !validCatalogTarget(catalogTarget, availableProjects, isAdmin)} onClick={() => void deploySelectedModel()}>{t("omnimizer.wizard.deploy")}</Button>
        </DialogActions>
      </Dialog>
      <Drawer
        anchor={isRTL ? "left" : "right"}
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: { width: { xs: "85%", sm: "450px" } },
        }}
      >
        {renderModelMetadata()}
      </Drawer>

      {/* Admin form dialog */}
      <PredefinedDeploymentForm
        open={formOpen}
        onClose={handleCloseForm}
        editModel={editModel}
      />

      {/* Admin menu */}
      <Menu
        id="model-menu"
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        MenuListProps={{
          "aria-labelledby": "model-options-button",
        }}
      >
        <MenuItem onClick={handleEditModel}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("common.edit")}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ color: "error" }}>
            {t("common.delete")}
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>{t("predefinedModels.deleteDialogTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("predefinedModels.deleteDialogConfirm", {
              name: menuModel?.name,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="secondary" variant="text">
            {t("common.cancel")}
          </Button>
          <Button
            color="error"
            startIcon={
              deletingModelId ? <CircularProgress size={20} /> : <DeleteIcon />
            }
            onClick={handleDeleteConfirm}
            disabled={!!deletingModelId}
          >
            {deletingModelId ? t("common.deleting") : t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default PredefinedModels;
